import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { my } from "@store/actions/user";
import { useAppDispatch } from "@store/hooks";
import instance from "@store/middleware/api";

import { useAuth } from "@/hooks/auth";
import { type DowngradeChannelItem,DowngradeChannelPickerModal } from "@/pages/AccountLicensesPage/LicensesTab";

/**
 * Site-wide mandatory modal that auto-opens whenever the logged-in user
 * has a pending channel downgrade with no keep-list confirmed yet.
 *
 * Purpose: the keep-list selection drives which YouTube channels get
 * disconnected at the next billing cycle. Without a confirmation the
 * backend defers all channel cleanup AND pauses claim processing, so
 * leaving this unset has real impact on the user's plan. Surface it
 * everywhere — new tabs, fresh logins, any page the user lands on —
 * until they pick.
 *
 * Refetch triggers:
 *   - auth state change (userId from useAuth)
 *   - route change (pathname)
 *   - `pending-downgrade-changed` custom window event (fired by callers
 *     that mutate pending state — Schedule Downgrade, Keep Current Plan,
 *     Upgrade, etc. — so the modal opens/closes synchronously without
 *     waiting for the next nav).
 *
 * The picker itself (DowngradeChannelPickerModal) has no close button —
 * the user must either confirm a selection or click Keep Current Plan
 * to cancel the whole downgrade.
 */
const PendingDowngradeGlobalModal: React.FC = () => {
  const { pathname } = useLocation();
  const { myData } = useAuth();
  const dispatch = useAppDispatch();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;

  const [open, setOpen] = useState(false);
  const [subId, setSubId] = useState<string | null>(null);
  const [newLimit, setNewLimit] = useState(1);
  const [effectiveDate, setEffectiveDate] = useState<string>("");
  const [channels, setChannels] = useState<DowngradeChannelItem[]>([]);

  // Session-scoped dismissal: once the user closes the modal via the X
  // button, we stop auto-reopening it on every route change. The reminder
  // banner (DisconnectChannelsReminderBanner) stays visible site-wide so
  // the user always has a one-click path back, and the `pending-downgrade-
  // open-picker` event from that banner clears the flag + reopens. Scoped
  // per-sub-id so a fresh downgrade cycle later gets its own state.
  const dismissedKey = subId ? `pending-downgrade-modal-dismissed-${subId}` : null;
  const _isDismissedForSession = () =>
    dismissedKey ? sessionStorage.getItem(dismissedKey) === "1" : false;

  const refetch = useCallback(async (opts?: { forceOpen?: boolean }) => {
    if (!userId) { setOpen(false); return; }
    try {
      const subResp = await instance.get("/subscriptions/my");
      const sub: any = subResp?.data;
      if (!sub) { setOpen(false); return; }
      const chResp = await instance.get("/user-channels");
      const items: DowngradeChannelItem[] = (chResp?.data?.channels || []).map((c: any) => ({
        id: c.id,
        channelName: c.channelName || c.youtubeChannelId,
        channelHandle: c.channelHandle || "",
        channelThumbnailUrl: c.channelThumbnailUrl || null,
      }));
      // Global modal only fires for the POST-rollover over-quota state —
      // user already paid the lower price + claims are paused. Pre-rollover
      // (downgrade scheduled, not yet applied) is handled by the in-page
      // Manage Add-ons flow; we don't want a blocking site-wide modal in
      // that state since the user can simply wait or cancel via the
      // in-page UI. Pure data-shape detection: user has more connected
      // channels than the active sub cap.
      const isPostRolloverOverQuota = items.length > sub.channels;

      if (!isPostRolloverOverQuota) { setOpen(false); return; }
      setSubId(sub.id);
      setNewLimit(sub.channels);
      setEffectiveDate(sub.currentPeriodEnd
        ? new Date(sub.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
        : "");
      setChannels(items);
      const dismissed = sessionStorage.getItem(`pending-downgrade-modal-dismissed-${sub.id}`) === "1";
      setOpen(opts?.forceOpen || !dismissed);
    } catch {
      setOpen(false);
    }
  }, [userId]);

  useEffect(() => { refetch(); }, [refetch, pathname]);
  useEffect(() => {
    const changed = () => refetch();
    const openPicker = () => {
      if (dismissedKey) sessionStorage.removeItem(dismissedKey);
      refetch({ forceOpen: true });
    };
    // Tab-focus refetch: catches "user came back to the tab and a Stripe
    // webhook flipped state while they were away" without waiting for a
    // route change.
    const onFocus = () => { void refetch(); };
    window.addEventListener("pending-downgrade-changed", changed);
    window.addEventListener("pending-downgrade-open-picker", openPicker);
    window.addEventListener("focus", onFocus);
    // 60s in-tab polling: catches state changes that happen while the
    // user is just sitting on a page (cycle rollover at exactly midnight,
    // admin force-apply, etc.) without needing an explicit user signal.
    const pollId = window.setInterval(() => { void refetch(); }, 60_000);
    return () => {
      window.removeEventListener("pending-downgrade-changed", changed);
      window.removeEventListener("pending-downgrade-open-picker", openPicker);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(pollId);
    };
  }, [refetch, dismissedKey]);

  const handleDismiss = useCallback(() => {
    if (dismissedKey) sessionStorage.setItem(dismissedKey, "1");
    setOpen(false);
  }, [dismissedKey]);

  // Global user-state refresh — re-fetches the redux user store on every
  // route change AND on window focus. Without this, plan transitions
  // driven by Stripe webhooks (cancellation → free, downgrade applied,
  // billing-period switched) only land in the UI on the next hard
  // reload — the user can keep seeing a stale `myData.plan` and stale
  // page layouts (e.g. the subscription claims page rendering even
  // though the sub just ended). Cheap GET; runs unconditionally as
  // long as the user is logged in.
  useEffect(() => {
    if (!userId) return;
    void dispatch(my());
  }, [dispatch, userId, pathname]);
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => { void dispatch(my()); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, userId]);

  const handleConfirm = useCallback(async (keepChannelIds: string[]) => {
    if (!subId) { setOpen(false); return; }
    try {
      await instance.post(`/subscriptions/${subId}/downgrade-channels`, { keepChannelIds });
      // Clear any session dismissal so the picker doesn't keep flagging
      // this sub state — the keep-list is now confirmed.
      if (dismissedKey) sessionStorage.removeItem(dismissedKey);
      // Flag the just-completed disconnect so DowngradeSuccessBanner
      // surfaces a persistent green confirmation. Per-timestamp scoped
      // so a future disconnect re-shows it.
      try {
        localStorage.setItem("downgrade-channels-confirmed-at", new Date().toISOString());
      } catch { /* non-fatal */ }
      window.dispatchEvent(new Event("pending-downgrade-changed"));
      window.dispatchEvent(new Event("downgrade-channels-confirmed"));
    } catch (err) {
      console.error("Save keep-list failed:", err);
    } finally {
      setOpen(false);
    }
  }, [subId, dismissedKey]);

  // `handleCancelDowngrade` was used pre-2026-05-27 when this modal also
  // fired for the PRE-rollover scheduled-downgrade state (Keep Current
  // Plan button). The global modal now only fires POST-rollover, where
  // a back-out doesn't apply — pre-rollover cancellation is reached via
  // the in-page Manage Add-ons flow instead.

  return (
    <DowngradeChannelPickerModal
      open={open}
      channels={channels}
      newLimit={newLimit}
      effectiveDate={effectiveDate}
      onConfirm={handleConfirm}
      onClose={handleDismiss}
      alreadyApplied
    />
  );
};

export default PendingDowngradeGlobalModal;
